import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
};

export default function PropertyModalMobile({ open, onClose, children, className }: Props) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !contentRef.current) return;
    const el = contentRef.current;

    const stopPropagation = (event: Event) => {
      event.stopPropagation();
    };

    el.addEventListener('touchstart', stopPropagation, { passive: true });
    el.addEventListener('touchmove', stopPropagation, { passive: false });
    el.addEventListener('wheel', stopPropagation, { passive: false });

    return () => {
      el.removeEventListener('touchstart', stopPropagation);
      el.removeEventListener('touchmove', stopPropagation);
      el.removeEventListener('wheel', stopPropagation);
    };
  }, [open]);

  if (!open) return null;

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/30" />
      <div
        ref={contentRef}
        className={clsx(
          'relative z-10 w-full max-w-[640px] max-h-[calc(100dvh-32px)] bg-white rounded-3xl shadow-[0_24px_48px_rgba(15,23,42,0.28)]',
          'overflow-y-auto overscroll-contain ios-touch-fix touch-pan-y',
          'p-0',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
